import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { livpureMockData } from '../data';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar, ChevronDown } from 'lucide-react';

// Dashboard metrics card component
const MetricCard = ({ title, value, icon, trend, trendPercentage }) => {
  const trendColor = trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-gray-500';
  const trendIcon = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <div className="p-2 bg-blue-50 rounded-lg">{icon}</div>
      </div>
      <div className="flex items-end space-x-2">
        <span className="text-2xl font-bold">{value}</span>
        <div className={`text-sm flex items-center ${trendColor}`}>
          <span>{trendIcon}</span>
          <span className="ml-1">{trendPercentage}%</span>
        </div>
      </div>
    </div>
  );
};

// AI Insight card component
const InsightCard = ({ title, description, priority, category, action }) => {
  const priorityColors = {
    high: 'border-red-500 bg-red-50',
    medium: 'border-yellow-500 bg-yellow-50',
    low: 'border-blue-500 bg-blue-50'
  };
  
  const priorityTextColors = {
    high: 'text-red-600',
    medium: 'text-yellow-600',
    low: 'text-blue-600'
  };
  
  return (
    <div className={`border-l-4 ${priorityColors[priority]} p-4 rounded-r-lg mb-3`}>
      <div className="flex justify-between mb-1">
        <h4 className="font-medium text-gray-800">{title}</h4>
        <span className={`text-xs font-medium ${priorityTextColors[priority]} px-2`}>
          {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-2">{description}</p>
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-500">Category: {category}</span>
        <button className="text-xs bg-white border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50">
          {action}
        </button>
      </div>
    </div>
  );
};

// Category Pulse card component
const CategoryPulseCard = ({ category, positivePercentage, reviewCount, trend }) => {
  const getStatusColor = (percentage) => {
    if (percentage >= 70) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <span className="text-green-500">↑</span>;
    if (trend === 'down') return <span className="text-red-500">↓</span>;
    return <span className="text-gray-500">→</span>;
  };
  
  return (
    <div className="flex items-center p-3 border border-gray-200 rounded-lg mb-2">
      <div className="mr-3">
        <div className="w-2 h-10 rounded-full bg-gray-200">
          <div 
            className={`w-2 rounded-full ${getStatusColor(positivePercentage)}`} 
            style={{ height: `${positivePercentage}%` }}
          ></div>
        </div>
      </div>
      <div className="flex-grow">
        <div className="flex justify-between">
          <h4 className="text-sm font-medium">{category}</h4>
          <div className="flex items-center text-xs">
            {getTrendIcon(trend)}
            <span className="ml-1">{positivePercentage}%</span>
          </div>
        </div>
        <div className="text-xs text-gray-500">
          {reviewCount} reviews
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
    // Get current date
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const [timeRange, setTimeRange] = useState('7days'); // '7days', '30days', '90days', 'year'
    const [dateRange, setDateRange] = useState([sevenDaysAgo, today]); // Default to last 7 days
    const [showDatePicker, setShowDatePicker] = useState(false);
    const datePickerRef = useRef(null);

    // Common date presets
    const datePresets = [
      { label: 'Last 7 days', value: '7days', startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), endDate: today },
      { label: 'Last 30 days', value: '30days', startDate: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), endDate: today },
      { label: 'Last 90 days', value: '90days', startDate: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), endDate: today },
      { label: 'This year', value: 'year', startDate: new Date(today.getFullYear(), 0, 1), endDate: today }
    ];

  // Calculate some metrics from the mock data
  const totalReviews = livpureMockData.reviews.length;
  
  // Calculate average rating
  const sumRatings = livpureMockData.reviews.reduce((sum, review) => sum + review.rating, 0);
  const avgRating = (sumRatings / totalReviews).toFixed(1);
  
  // Calculate positive sentiment percentage
  const positiveReviews = livpureMockData.reviews.filter(review => review.sentiment === 'Positive').length;
  const positiveSentimentPercentage = Math.round((positiveReviews / totalReviews) * 100);
  
  // Calculate reply rate
  const repliedReviews = livpureMockData.reviews.filter(review => review.replyStatus === 'Replied').length;
  const replyRate = Math.round((repliedReviews / totalReviews) * 100);
  
  // Get 3 most recent reviews
  const recentReviews = [...livpureMockData.reviews]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);
  
  // Calculate metrics by category
  const categoryStats = {};
  livpureMockData.reviews.forEach(review => {
    review.categorySentiments.forEach(cs => {
      if (!categoryStats[cs.category]) {
        categoryStats[cs.category] = {
          total: 0,
          positive: 0,
          negative: 0,
          neutral: 0
        };
      }
      
      categoryStats[cs.category].total++;
      
      if (cs.sentiment === 'Positive') {
        categoryStats[cs.category].positive++;
      } else if (cs.sentiment === 'Negative') {
        categoryStats[cs.category].negative++;
      } else {
        categoryStats[cs.category].neutral++;
      }
    });
  });
  
  // Convert to array and calculate percentages
  const categoryStatsArray = Object.entries(categoryStats).map(([category, stats]) => ({
    category,
    total: stats.total,
    positivePercentage: Math.round((stats.positive / stats.total) * 100),
    negativePercentage: Math.round((stats.negative / stats.total) * 100),
    // Add mock trend for demo purposes
    trend: Math.random() > 0.5 ? 'up' : 'down'
  })).sort((a, b) => b.total - a.total);
  
  // Get pending negatives (reviews with negative sentiment that haven't been replied to)
  const pendingNegatives = livpureMockData.reviews.filter(
    review => review.sentiment === 'Negative' && review.replyStatus === 'Not Replied'
  ).length;
  
  // Mock AI insights
  const aiInsights = [
    {
      title: 'Customer Service Response Time Issues',
      description: 'Negative sentiment around response times has increased by 23% in the last 30 days.',
      priority: 'high',
      category: 'Customer Service',
      action: 'View Analysis'
    },
    {
      title: 'Positive Feedback on App Updates',
      description: 'Recent app updates have resulted in a 15% increase in positive reviews.',
      priority: 'low',
      category: 'App Experience',
      action: 'See Details'
    },
    {
      title: 'Product Quality Concerns in East Region',
      description: 'Several customers in the East region have mentioned quality issues with the latest batch.',
      priority: 'medium',
      category: 'Product Quality',
      action: 'Investigate'
    }
  ];
  
  // Platform distribution
  const platformData = [
    { name: 'Google Reviews', count: 55, percentage: 27.5 },
    { name: 'App Store', count: 42, percentage: 21 },
    { name: 'Play Store', count: 48, percentage: 24 },
    { name: 'Amazon', count: 55, percentage: 27.5 }
  ];
  
  // Top performing locations
  const topLocations = [
    { name: 'Mumbai', reviewCount: 45, positivePercentage: 82 },
    { name: 'Delhi', reviewCount: 38, positivePercentage: 78 },
    { name: 'Bangalore', reviewCount: 42, positivePercentage: 75 },
    { name: 'Chennai', reviewCount: 35, positivePercentage: 72 },
    { name: 'Hyderabad', reviewCount: 30, positivePercentage: 70 }
  ];

  // Determine date range display text
  const getDateRangeText = () => {
    if (!dateRange[0] && !dateRange[1]) return 'Select date range';
    
    // Check if current selection matches a preset
    for (const preset of datePresets) {
      if (
        dateRange[0]?.toDateString() === preset.startDate.toDateString() && 
        dateRange[1]?.toDateString() === preset.endDate.toDateString()
      ) {
        return preset.label;
      }
    }
    
    // Custom date range
    if (dateRange[0] && dateRange[1]) {
      return `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`;
    } else if (dateRange[0]) {
      return `From ${dateRange[0].toLocaleDateString()}`;
    } else if (dateRange[1]) {
      return `Until ${dateRange[1].toLocaleDateString()}`;
    }
    
    return 'Select date range';
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setDateRange([start, end]);
  };

  const applyDatePreset = (preset) => {
    setTimeRange(preset.value);
    setDateRange([new Date(preset.startDate), new Date(preset.endDate)]);
    setShowDatePicker(false);
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
          <p className="text-sm text-gray-600">Welcome to Reviews Analytics</p>
        </div>
        {/* Date Range Selector positioned at top right */}
        <div className="relative" ref={datePickerRef}>
          <div 
            className="flex items-center bg-white shadow-sm rounded-lg border border-gray-200 px-4 py-2 cursor-pointer hover:border-blue-500"
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            <Calendar className="mr-2 h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">{getDateRangeText()}</span>
            <ChevronDown className="ml-2 h-4 w-4 text-gray-500" />
          </div>
          
          {/* Date Range Dropdown Panel */}
          {showDatePicker && (
            <div className="absolute z-50 mt-1 right-0 w-auto min-w-max bg-white rounded-md shadow-lg border border-gray-200">
              {/* Quick Select Presets (Horizontal) */}
              <div className="p-3 border-b border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {datePresets.map((preset, index) => (
                    <button
                      key={index}
                      className={`text-sm px-3 py-1 rounded ${
                        timeRange === preset.value 
                          ? 'bg-blue-100 text-blue-700 font-medium' 
                          : 'bg-gray-100 hover:bg-blue-50 hover:text-blue-600'
                      }`}
                      onClick={() => applyDatePreset(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Calendar Range Picker */}
              <div className="p-3">
                <DatePicker
                  selected={dateRange[0]}
                  onChange={handleDateRangeChange}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  selectsRange
                  inline
                  monthsShown={2}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 border-t border-gray-200 bg-gray-50">
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setDateRange([null, null])}
                >
                  Clear
                </button>
                <button
                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  onClick={() => setShowDatePicker(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Attention needed alert */}
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Attention Required</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>There are {pendingNegatives} unanswered negative reviews that require immediate attention.</p>
            </div>
            <div className="mt-4">
              <div className="-mx-2 -my-1.5 flex">
                <Link to="/reviews?sentiment=negative&status=unreplied" className="px-3 py-1.5 bg-red-100 text-red-800 text-xs rounded-md hover:bg-red-200">
                  View Critical Reviews
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard 
          title="Total Reviews" 
          value={totalReviews.toString()} 
          icon={
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          } 
          trend="up" 
          trendPercentage="12" 
        />
        <MetricCard 
          title="Average Rating" 
          value={avgRating} 
          icon={
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          } 
          trend="up" 
          trendPercentage="5" 
        />
        <MetricCard 
          title="Positive Sentiment" 
          value={`${positiveSentimentPercentage}%`} 
          icon={
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          } 
          trend="up" 
          trendPercentage="3" 
        />
        <MetricCard 
          title="Reply Rate" 
          value={`${replyRate}%`} 
          icon={
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          } 
          trend="down" 
          trendPercentage="2" 
        />
      </div>
      
      {/* Three-column layout for insights, categories, and recent reviews */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* AI Insights */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">AI Insights</h2>
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">New</span>
          </div>
          <div className="space-y-2">
            {aiInsights.map((insight, index) => (
              <InsightCard 
                key={index}
                title={insight.title}
                description={insight.description}
                priority={insight.priority}
                category={insight.category}
                action={insight.action}
              />
            ))}
          </div>
          <Link 
            to="/insights" 
            className="mt-4 text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            View all insights
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </Link>
        </div>
        
        {/* Category Pulse */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">Category Pulse</h2>
            <Link to="/insight" className="text-sm text-blue-600 hover:text-blue-800">View Analysis</Link>
          </div>
          <div>
            {categoryStatsArray.slice(0, 5).map((category, index) => (
              <CategoryPulseCard
                key={index}
                category={category.category}
                positivePercentage={category.positivePercentage}
                reviewCount={category.total}
                trend={category.trend}
              />
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <Link to="/insights" className="text-sm bg-gray-50 border border-gray-200 text-gray-700 rounded-md px-4 py-2 hover:bg-gray-100 block text-center">
              View Category Insights
            </Link>
          </div>
        </div>
        
        {/* Recent Reviews */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Reviews</h2>
            <Link to="/reviews" className="text-sm text-blue-600 hover:text-blue-800">View All</Link>
          </div>
          <div className="space-y-4">
            {recentReviews.map((review, index) => (
              <div key={review.id} className={index < recentReviews.length - 1 ? "border-b border-gray-200 pb-4" : ""}>
                <div className="flex justify-between mb-2">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                      {review.userName.split(' ').map(name => name[0]).join('')}
                    </div>
                    <div className="ml-2">
                      <p className="text-sm font-medium">{review.userName}</p>
                      <p className="text-xs text-gray-500">{review.source} • {review.region}, {review.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-700 mb-2 line-clamp-2">{review.reviewText}</p>
                <div className="flex justify-between items-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    review.replyStatus === 'Replied' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {review.replyStatus}
                  </span>
                  <Link to={`/reviews/${review.id}`} className="text-xs text-blue-600">View</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Two-column layout for platform distribution and top locations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Platform distribution */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Reviews by Platform</h2>
          <div className="grid grid-cols-2 gap-4">
            {platformData.map((platform, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                <div className="flex items-center mb-2">
                  <div className="h-6 w-6 mr-2 rounded-full bg-white p-1 flex items-center justify-center">
                    {platform.name === 'Google Reviews' && (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#FFC107" />
                        <path d="M3.15295 7.3455L6.43845 9.755C7.32745 7.554 9.48045 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15895 2 4.82795 4.1685 3.15295 7.3455Z" fill="#FF3D00" />
                        <path d="M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5718 17.5742 13.3038 18.001 12 18C9.39903 18 7.19053 16.3415 6.35853 14.027L3.09753 16.5395C4.75253 19.778 8.11353 22 12 22Z" fill="#4CAF50" />
                        <path d="M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.785L18.7045 19.4035C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#1976D2" />
                      </svg>
                    )}
                    {platform.name === 'App Store' && (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM16.5 15.5C16.5 16.1 16.1 16.5 15.5 16.5H8.5C7.9 16.5 7.5 16.1 7.5 15.5V8.5C7.5 7.9 7.9 7.5 8.5 7.5H15.5C16.1 7.5 16.5 7.9 16.5 8.5V15.5Z" fill="#0D96F6"/>
                      </svg>
                    )}
                    {platform.name === 'Play Store' && (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.18 20.83L12 12l-8.82-8.83C2.7 3.63 2.32 4.28 2 5v14c.32.72.7 1.37 1.18 1.83z" fill="#EA4335"/>
                        <path d="M12 12l-8.82 8.83c.48.46 1.03.84 1.66 1.14L16.15 12l-11.31-9.97c-.63.3-1.18.68-1.66 1.14L12 12z" fill="#FBBC04"/>
                        <path d="M20.82 12L12 3.17l-7.31 6.39L12 12l-7.31 2.44L12 20.83L20.82 12z" fill="#4285F4"/>
                        <path d="M12 12l8.82 8.83c.48-.46.86-1.01 1.16-1.64L13.66 12l8.32-7.19c-.3-.63-.68-1.18-1.16-1.64L12 12z" fill="#34A853"/>
                      </svg>
                    )}
                    {platform.name === 'Amazon' && (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.3 11.6C14.9 11.2 14.5 11 14.1 11C13.7 11 13.3 11.2 12.9 11.6C12.6 12 12.5 12.3 12.5 12.7C12.5 13.1 12.6 13.4 12.9 13.8C13.2 14.2 13.6 14.4 14 14.4C14.4 14.4 14.8 14.2 15.2 13.8C15.5 13.4 15.7 13.1 15.7 12.7C15.7 12.3 15.6 12 15.3 11.6Z" fill="#FF9900"/>
                        <path d="M20.8 17.7C20.4 17.9 18.6 18.9 17.8 19.3C15.3 20.5 12.5 20.5 8.3 18.4C8 18.2 8 18.2 7.5 18C5.3 16.7 3.3 14 3.3 11C3.3 9 4 7.1 5.3 5.5C6.2 4.5 7.2 3.7 8.5 3.1C8.7 3 8.8 3 8.8 3.2C8.8 3.4 8.8 3.6 8.8 4C8.8 4.2 8.7 4.3 8.5 4.4C6.5 5.2 5.4 7.2 5.4 9.5C5.4 12.6 7.5 15.7 10.5 16.8C14.7 18.2 19.5 17 21.1 14.1C22.1 12.2 22.1 9.5 20.8 7.5C19.9 6.2 18.5 5.4 17 5.2C16.7 5.2 16.4 5.1 16.1 5.1C15.8 5.1 15.6 5 15.7 4.6C15.8 4.2 16.4 4 16.7 4C16.8 4 16.9 4 17 4C18.5 4.2 19.8 4.8 20.8 5.8C23.5 8.5 23.5 13 20.7 15.7C20.3 16 19.1 16.8 18.6 17C18.5 17 18.5 17 18.5 17C18.5 17 18.6 16.9 18.8 16.9C19.1 16.8 19.5 16.8 19.8 16.8C20.5 16.8 21.1 16.9 21.8 17.1C22.1 17.2 22.1 17.2 21.8 17.5C21.6 17.6 21.1 17.6 20.8 17.7Z" fill="#FF9900"/>
                      </svg>
                    )}
                  </div>
                  <h3 className="font-medium text-sm">{platform.name}</h3>
                </div>
                <div className="flex justify-between items-end">
                  <span className="text-2xl font-bold">{platform.count}</span>
                  <span className="text-sm text-gray-500">{platform.percentage}%</span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${platform.percentage}%` }}></div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <h3 className="text-sm font-medium mb-3">Source Interaction Stats</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Rating</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reply Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Google</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">4.3</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">86%</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">App Store</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">4.1</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">72%</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Play Store</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">3.9</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">68%</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Amazon</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">4.5</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">78%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Top locations and actions */}
        <div className="grid grid-cols-1 gap-6">
          {/* Top locations */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Top Performing Locations</h2>
              <Link to="/locations" className="text-sm text-blue-600 hover:text-blue-800">View All</Link>
            </div>
            {topLocations.map((location, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center">
                  <div className={`w-1.5 h-8 rounded-full mr-3 ${
                    location.positivePercentage >= 80 ? 'bg-green-500' :
                    location.positivePercentage >= 70 ? 'bg-green-400' :
                    'bg-green-300'
                  }`}></div>
                  <div>
                    <span className="text-sm font-medium text-gray-800">{location.name}</span>
                    <div className="text-xs text-gray-500">{location.reviewCount} reviews</div>
                  </div>
                </div>
                <div className="text-sm font-medium text-green-600">{location.positivePercentage}%</div>
              </div>
            ))}
            <div className="mt-4 pt-2">
              <Link 
                to="/locations" 
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center"
              >
                View location performance map
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </Link>
            </div>
          </div>
          
          {/* Quick actions */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/reviews?status=unreplied" className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <svg className="w-6 h-6 text-blue-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <span className="text-xs font-medium text-gray-700">Pending Replies</span>
                <span className="text-sm font-bold text-gray-900">{totalReviews - repliedReviews}</span>
              </Link>
              
              <Link to="/insights" className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <svg className="w-6 h-6 text-purple-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-xs font-medium text-gray-700">AI Insights</span>
                <span className="text-sm font-bold text-gray-900">3 New</span>
              </Link>
              
              <Link to="/reports" className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <svg className="w-6 h-6 text-green-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-xs font-medium text-gray-700">Generate Report</span>
              </Link>
              
              <Link to="/settings" className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <svg className="w-6 h-6 text-gray-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-xs font-medium text-gray-700">Settings</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sentiment trend chart */}
      {/* Sentiment trend chart with fixed container */}
<div className="bg-white rounded-lg shadow-sm p-6 mb-8">
  <div className="flex items-center justify-between mb-6">
    <h2 className="text-lg font-medium text-gray-900">Sentiment Trend</h2>
    <div className="flex items-center space-x-2">
      <div className="flex items-center">
        <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
        <span className="text-xs text-gray-600">Positive</span>
      </div>
      <div className="flex items-center">
        <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
        <span className="text-xs text-gray-600">Negative</span>
      </div>
      <div className="flex items-center">
        <div className="w-3 h-3 bg-gray-300 rounded-full mr-1"></div>
        <span className="text-xs text-gray-600">Neutral</span>
      </div>
    </div>
  </div>
  
  {/* Chart container with fixed dimensions */}
  <div className="relative w-full h-64 mb-4">
    {/* Grid lines */}
    <div className="absolute inset-0">
      {[0, 25, 50, 75, 100].map((val, i) => (
        <div 
          key={i} 
          className="absolute w-full border-t border-gray-200"
          style={{ top: `${100 - val}%` }}
        >
          <span className="absolute -mt-2 -ml-8 text-xs text-gray-400">{val}%</span>
        </div>
      ))}
    </div>
    
    {/* Chart data visualization */}
    <div className="absolute inset-0 mt-4">
      {/* Positive trend line */}
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="positiveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(34, 197, 94, 0.5)" />
            <stop offset="100%" stopColor="rgba(34, 197, 94, 0)" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        <path 
          d="M0,75 C10,70 20,60 30,55 C40,50 50,45 60,50 C70,55 80,40 90,30 L100,35 V100 H0 Z" 
          fill="url(#positiveGradient)" 
        />
        {/* Line */}
        <path 
          d="M0,75 C10,70 20,60 30,55 C40,50 50,45 60,50 C70,55 80,40 90,30 L100,35" 
          fill="none" 
          stroke="#22c55e" 
          strokeWidth="2"
        />
      </svg>
      
      {/* Negative trend line */}
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <linearGradient id="negativeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(239, 68, 68, 0.3)" />
            <stop offset="100%" stopColor="rgba(239, 68, 68, 0)" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        <path 
          d="M0,85 C10,82 20,80 30,85 C40,90 50,88 60,85 C70,82 80,80 90,75 L100,78 V100 H0 Z" 
          fill="url(#negativeGradient)" 
        />
        {/* Line */}
        <path 
          d="M0,85 C10,82 20,80 30,85 C40,90 50,88 60,85 C70,82 80,80 90,75 L100,78" 
          fill="none" 
          stroke="#ef4444" 
          strokeWidth="2"
        />
      </svg>
    </div>
    
    {/* X-axis labels */}
    <div className="absolute bottom-0 w-full flex justify-between text-xs text-gray-500">
      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
        <div key={i} className="text-center" style={{ width: '14.28%' }}>
          {day}
        </div>
      ))}
    </div>
  </div>
  
  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center">
    <Link 
      to="/analytics" 
      className="text-sm bg-blue-50 text-blue-600 rounded-md px-4 py-2 hover:bg-blue-100"
    >
      View Detailed Analytics
    </Link>
  </div>
</div>
    </div>
  );
};

export default Dashboard;